import { useCallback, useEffect, useMemo, useState } from "react";

import { MAX_CONSECUTIVE_QUOTE_FAILURES, QUOTE_DEBOUNCE_MS } from "../constants/cryptoSwaps.constants";
import {
  isRangeRejection,
  normalizeCryptoSwapError,
  type NormalizedCryptoSwapError,
} from "../utils/cryptoSwapErrors";
import { createQuoteScheduler } from "../utils/cryptoSwapQuoteScheduler";

export interface PricedQuote {
  id: string;
  created_at: string;
  expires_at: string;
}

/** A quote and the idempotency key minted for it, always replaced together: a new price is a new operation. */
interface QuoteSlot<Q> {
  quote: Q;
  idempotencyKey: string;
  /** Identity of the form inputs this quote was requested for. */
  signature: string;
}

export interface RequestQuoteOptions {
  signature: string;
  immediate?: boolean;
}

interface QuoteJob<P> extends RequestQuoteOptions {
  payload: P;
}

export interface UseOtcQuoteResult<P, Q> {
  quote: Q | null;
  idempotencyKey: string | null;
  quoteSignature: string | null;
  isPricing: boolean;
  error: NormalizedCryptoSwapError | null;
  /** Auto-pricing stopped after repeated failures; the form waits for an explicit click. */
  isManualMode: boolean;
  /** Add to `Date.now()` to read the server's clock, estimated from the fresh quote's `created_at`. */
  clockOffsetMs: number;
  requestQuote: (payload: P, options: RequestQuoteOptions) => void;
  clearQuote: () => void;
}

/** Offsets beyond this are treated as a stale `created_at`, not as clock skew. */
const MAX_TRUSTED_SKEW_MS = 5 * 60_000;

/** The pricing loop both directions share. `price` is read once: pass a module-level service function. */
export function useOtcQuote<P, Q extends PricedQuote>(
  price: (payload: P, signal: AbortSignal) => Promise<Q>,
): UseOtcQuoteResult<P, Q> {
  const [slot, setSlot] = useState<QuoteSlot<Q> | null>(null);
  const [isPricing, setIsPricing] = useState(false);
  const [error, setError] = useState<NormalizedCryptoSwapError | null>(null);
  const [failureStreak, setFailureStreak] = useState(0);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);

  const [scheduler] = useState(() =>
    createQuoteScheduler<QuoteJob<P>, Q>({
      delayMs: QUOTE_DEBOUNCE_MS,
      run: (job, signal) => price(job.payload, signal),
      onResult: (quote, job) => {
        const serverNow = Date.parse(quote.created_at);
        const offset = Number.isFinite(serverNow) ? serverNow - Date.now() : 0;
        setClockOffsetMs(Math.abs(offset) <= MAX_TRUSTED_SKEW_MS ? offset : 0);
        setSlot({ quote, idempotencyKey: crypto.randomUUID(), signature: job.signature });
        setError(null);
        setFailureStreak(0);
        setIsPricing(false);
      },
      onError: (caught) => {
        const normalized = normalizeCryptoSwapError(caught, "quotes");
        setSlot(null);
        setError(normalized);
        // An amount outside the limits is the user's to fix, not an outage to back off from.
        setFailureStreak((streak) => (isRangeRejection(normalized) ? 0 : streak + 1));
        setIsPricing(false);
      },
    }),
  );

  useEffect(() => () => scheduler.cancel(), [scheduler]);

  const requestQuote = useCallback(
    (payload: P, options: RequestQuoteOptions) => {
      setIsPricing(true);
      setError(null);
      scheduler.schedule({ ...options, payload }, !!options.immediate);
    },
    [scheduler],
  );

  const clearQuote = useCallback(() => {
    scheduler.cancel();
    setSlot(null);
    setError(null);
    setIsPricing(false);
  }, [scheduler]);

  return useMemo(
    () => ({
      quote: slot?.quote ?? null,
      idempotencyKey: slot?.idempotencyKey ?? null,
      quoteSignature: slot?.signature ?? null,
      isPricing,
      error,
      isManualMode: failureStreak >= MAX_CONSECUTIVE_QUOTE_FAILURES,
      clockOffsetMs,
      requestQuote,
      clearQuote,
    }),
    [slot, isPricing, error, failureStreak, clockOffsetMs, requestQuote, clearQuote],
  );
}
